// src/pages/driver/DriverDetailsPage.js
import React, { useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Button, 
  Chip,
  Breadcrumbs,
  Link,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert
} from '@mui/material';
// Add import for the RFID card retrieval function
import {getDriverRFIDCards } from '../../api/driverAPI';

// Add these imports if not already present
import CreditCardIcon from '@mui/icons-material/CreditCard';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import ContactsIcon from '@mui/icons-material/Contacts';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PaymentIcon from '@mui/icons-material/Payment';
import { getDriver, deleteDriver } from '../../api/driverAPI';
import { getCompany } from '../../api/companyAPI';
import { getDriverGroup } from '../../api/driverGroupAPI';
import LoadingSpinner from '../../components/common/Loadingspinner';
import ErrorAlert from '../../components/common/ErrorAlert';
import CircularProgress from '@mui/material/CircularProgress';

const DriverDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const driverId = parseInt(id);

  // Fetch driver details
  const { data: driver, isLoading, isError, error } = useQuery({
    queryKey: ['driver', driverId],
    queryFn: () => getDriver(driverId),
    enabled: !!driverId && !isNaN(driverId)
  });

  // Fetch company details once we have the driver
  const { data: company, isLoading: isLoadingCompany } = useQuery({
    queryKey: ['company', driver?.DriverCompanyId],
    queryFn: () => getCompany(driver.DriverCompanyId),
    enabled: !!driverId && !!driver?.DriverCompanyId
  });

  // Fetch driver group details once we have the driver
  const { data: driverGroup, isLoading: isLoadingDriverGroup } = useQuery({
    queryKey: ['driverGroup', driver?.DriverGroupId],
    queryFn: () => getDriverGroup(driver.DriverGroupId),
    enabled: !!driver?.DriverGroupId
  });

  // Fetch RFID cards for this driver
  const { data: rfidCards, isLoading: isLoadingRFID, error: rfidError } = useQuery({
    queryKey: ['driverRFIDCards', driverId],
    queryFn: () => getDriverRFIDCards(driverId),
    enabled: !!driverId && !!driver
  });

  // Delete driver mutation
  const deleteMutation = useMutation({
    mutationFn: deleteDriver,
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'Driver deleted successfully',
        severity: 'success'
      });
      
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
      
      // Navigate back to drivers list after a short delay
      setTimeout(() => {
        navigate('/drivers');
      }, 1500);
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error deleting driver: ${error.message}`,
        severity: 'error'
      });
    }
  });

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteMutation.mutateAsync(driverId);
      setDeleteDialogOpen(false);
    } catch (error) {
      // Error is handled by the mutation
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Helper function to format contact information
  const formatContactInfo = () => {
    const contacts = [];
    if (driver?.DriverEmail) contacts.push(`📧 ${driver.DriverEmail}`);
    if (driver?.DriverPhone) contacts.push(`📞 ${driver.DriverPhone}`);
    return contacts.join(' | ') || 'No contact info';
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError || !driver) {
    return <ErrorAlert message={error instanceof Error ? error.message : 'Error loading driver'} />;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          {/* <Breadcrumbs aria-label="breadcrumb" mb={1}>
            <Link component={RouterLink} to="/" color="inherit">
              Dashboard
            </Link>
            <Link component={RouterLink} to="/drivers" color="inherit">
              Drivers
            </Link>
            <Typography color="textPrimary">{driver.DriverFullName}</Typography>
          </Breadcrumbs> */}
          <Typography variant="h4">{driver.DriverFullName}</Typography>
        </div>
        
        <Button 
          component={RouterLink} 
          to="/drivers" 
          startIcon={<ArrowBackIcon />}
        >
          Back to Drivers
        </Button>
      </Box>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<EditIcon />}
            component={RouterLink}
            to={`/drivers/${driver.DriverId}/edit`}
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
          >
            Delete
          </Button>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        {/* REORGANIZED LAYOUT - THREE ROWS WITH FOUR COLUMNS EACH */}
        
        {/* First Row: DriverID, Company, Group Name, Contact Information */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <PersonIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                Driver ID
              </Typography>
              <Typography variant="h6" color="primary">
                {driver.DriverId}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <BusinessIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                Company
              </Typography>
              <Typography variant="body1">
                {isLoadingCompany ? 'Loading...' : 
                 (company ? company.CompanyName : `Company ID: ${driver.DriverCompanyId}`)}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <GroupIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                Group Name
              </Typography>
              <Typography variant="body1">
                {isLoadingDriverGroup ? 'Loading...' :
                 (driverGroup ? driverGroup.DriversGroupName : 
                  (driver.DriverGroupId ? `Group ID: ${driver.DriverGroupId}` : 'No group assigned'))}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <ContactsIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                Contact Information
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {formatContactInfo()}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Second Row: Status, Action Notifications, Payment Notifications, System Notification */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Status
              </Typography>
              <Chip 
                label={driver.DriverEnabled ? "Enabled" : "Disabled"} 
                color={driver.DriverEnabled ? "success" : "default"}
                size="small"
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Action Notifications
              </Typography>
              <Chip 
                label={driver.DriverNotifActions ? "Enabled" : "Disabled"} 
                color={driver.DriverNotifActions ? "success" : "default"}
                size="small"
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Payment Notifications
              </Typography>
              <Chip 
                label={driver.DriverNotifPayments ? "Enabled" : "Disabled"} 
                color={driver.DriverNotifPayments ? "success" : "default"}
                size="small"
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                System Notifications
              </Typography>
              <Chip 
                label={driver.DriverNotifSystem ? "Enabled" : "Disabled"} 
                color={driver.DriverNotifSystem ? "success" : "default"}
                size="small"
              />
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Third Row: Created At, Updated At (2 columns, 2 empty) */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Created At
              </Typography>
              <Typography variant="body1">
                {driver.DriverCreated ? 
                  new Date(driver.DriverCreated).toLocaleString() : 
                  'Not available'
                }
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Updated At
              </Typography>
              <Typography variant="body1">
                {driver.DriverUpdated ? 
                  new Date(driver.DriverUpdated).toLocaleString() : 
                  'Not available'
                }
              </Typography>
            </Box>
          </Grid>

          {/* Empty columns for consistency */}
          <Grid item xs={12} sm={6} md={3}>
            {/* Empty column */}
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            {/* Empty column */}
          </Grid>
        </Grid>
      </Paper>

      {/* RFID Cards Section */}
      {rfidCards && rfidCards.length > 0 && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            <CreditCardIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            RFID Cards
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Card Number</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rfidCards.map((card) => (
                  <TableRow key={card.RFIDCardId}>
                    <TableCell>{card.RFIDCardNumber}</TableCell>
                    <TableCell>
                      <Chip 
                        label={card.RFIDCardEnabled ? "Active" : "Inactive"} 
                        color={card.RFIDCardEnabled ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{new Date(card.RFIDCardCreated).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Driver
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete driver "{driver.DriverFullName}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DriverDetailsPage;